import React, { useState, useEffect } from 'react';
import { dataApi } from '../../services/api';
import { toast } from 'sonner';
import { Check } from 'lucide-react';

// shadcn UI components
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';

// Role definitions with descriptions
const ROLES = [
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full system access including user management and settings'
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Manage quotes, jobs, and customers; view reports'
  },
  {
    value: 'receptionist',
    label: 'Receptionist',
    description: 'Handle customer inquiries, create quotes, and manage bookings'
  },
  {
    value: 'tradesperson',
    label: 'Tradesperson',
    description: 'View and update assigned jobs and tasks'
  },
  {
    value: 'viewer',
    label: 'Viewer',
    description: 'Read-only access to quotes and customer information'
  }
];

const UserForm = ({ isOpen, onClose, onUserCreated }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    roles: []
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        roles: []
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const toggleRole = (role) => {
    setFormData(prev => {
      const roles = prev.roles.includes(role)
        ? prev.roles.filter(r => r !== role)
        : [...prev.roles, role];
      return { ...prev, roles };
    });
    // Clear role error when user selects a role
    if (errors.roles) {
      setErrors(prev => ({ ...prev, roles: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email is mandatory
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // At least one role must be selected
    if (formData.roles.length === 0) {
      newErrors.roles = 'At least one role must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);

      // Create user using dataApi
      const newUser = await dataApi.userCreate({
        email: formData.email.trim().toLowerCase(),
        firstName: formData.first_name.trim() || null,
        lastName: formData.last_name.trim() || null,
        phone: formData.phone.trim() || null,
        roles: formData.roles
      });

      if (newUser.emailSent) {
        toast.success(`Invitation sent to ${newUser.email || formData.email.trim()}`);
      } else if (newUser.emailSkippedReason === 'org-daily-limit') {
        toast.warning('User added. Your organisation has reached today\'s limit on invitation emails, so none was sent — you can resend it tomorrow.');
      } else {
        toast.warning('User added, but the invitation email could not be sent. You can resend it from the list.');
      }
      
      // Call parent callback if provided
      if (onUserCreated) {
        onUserCreated(newUser);
      }

      // Close dialog
      onClose();
    } catch (error) {
      console.error('Error creating user:', error);
      // Handle duplicate email error
      if (error.message?.includes('duplicate') || error.code === 409) {
        toast.error('A user with this email address already exists');
      } else {
        toast.error(error.message || 'Failed to create user');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New User</DialogTitle>
          <DialogDescription>
            Add someone to your organisation. They'll get one email inviting them to sign in.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name Fields */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => handleInputChange('first_name', e.target.value)}
                placeholder="Enter first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => handleInputChange('last_name', e.target.value)}
                placeholder="Enter last name"
              />
            </div>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email">
              Email Address <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="user@example.com"
              className={errors.email ? 'border-destructive' : ''}
            />
            <p className="text-xs text-muted-foreground">
              This will be the email address they use to sign in
            </p>
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="Enter phone number"
            />
          </div>

          {/* Roles Selection */}
          <div className="space-y-2">
            <Label>
              Roles <span className="text-destructive">*</span>
            </Label>
            <p className="text-xs text-muted-foreground mb-3">
              Select one or more roles for this user
            </p>
            
            <div className={`border rounded-md ${errors.roles ? 'border-destructive' : ''}`}>
              <table className="w-full">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="w-12 px-4 py-2"></th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Role</th>
                    <th className="px-4 py-2 text-left text-sm font-semibold">Description</th>
                  </tr>
                </thead>
                <tbody>
                  {ROLES.map((role, index) => (
                    <tr
                      key={role.value}
                      className={`border-b last:border-b-0 hover:bg-muted/50 cursor-pointer transition-colors ${
                        formData.roles.includes(role.value) ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => toggleRole(role.value)}
                    >
                      <td className="px-4 py-3 text-center">
                        <div className={`h-4 w-4 rounded-sm border-2 flex items-center justify-center ${
                          formData.roles.includes(role.value) 
                            ? 'bg-primary border-primary' 
                            : 'border-input'
                        }`}>
                          {formData.roles.includes(role.value) && (
                            <Check className="h-3 w-3 text-primary-foreground" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-medium text-sm">{role.label}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {role.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {errors.roles && (
              <p className="text-xs text-destructive">{errors.roles}</p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Adding...' : 'Add User'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default UserForm;
